import { Injectable } from "@angular/core";
import { AngularFireDatabase } from "angularfire2/database";
import { Observable } from "rxjs/Observable";

@Injectable()
export class CityService {
    constructor(private db: AngularFireDatabase) { }
    existingCities: Array<any>;
    citiesData = {};

    getCities(): Observable<any> {
        return this.db.list(`/location/city`).valueChanges();
    }

    getStateCities(countryCode: string, stateId: string = ""): Observable<any> {
        if (stateId) {
            return this.db.object(`/location/city/${countryCode}/${stateId}`)
                .valueChanges();
        } else {
            return this.db.object(`/location/city/${countryCode}`)
                .valueChanges();
        }
    }

    delete(countryCode, stateId, city): boolean {
        try {
            this.db.list(`/location/city/${countryCode}/${stateId}/${city["id"]}`).remove();

            return true;
        } catch (e) {
            console.log(e);

            return false;
        }
    }

    getCityById(countryCode, stateId, cityId): Observable<{}> {
        return this.db.object(`/location/city/${countryCode}/${stateId}/${cityId}`).valueChanges();
    }

    async import(data: Array<any>): Promise<any> {
        try {
            const keys = data.shift();
            const existingCityNames = await this.getCityNames(data);
            const cities = await this.constructData(data, keys);
            const newCities = [];
            for (const city of cities) {
                if (!Object.keys(existingCityNames).length) {
                    await this.store(city);
                    newCities.push(city["cityName"]);
                }

                if (Object.keys(existingCityNames).length && existingCityNames[city["countryCode"]] &&
                    existingCityNames[city["countryCode"]][city["stateId"]]) {
                    if (!existingCityNames[city["countryCode"]][city["stateId"]].includes(city["cityName"])) {
                        await this.store(city);
                    }
                }
            }

            return true;
        } catch (e) {
            console.log(e);

            return false;
        }
    }

    private async constructData(data, keys): Promise<Array<string>> {
        const cities = [];
        data.forEach(values => {
            const city = {};
            keys.forEach((key, index) => {
                city[key] = values[index] ? values[index].trim() : "";
            });
            cities.push(city);
        });

        return cities;
    }

    private async store(city: any): Promise<any> {
        this.db
            .list(`/location/city/${city["countryCode"]}/${city["stateId"]}`)
            .push(city["cityName"]);
    }

    private async getCityNames(data: Array<any>): Promise<Array<string>> {
        return await new Promise<any>((resolve, reject) => {
            this.citiesData = {};
            data.forEach(element => {
                const countryCode = element[0];
                const stateId = element[1];
                this.getStateCities(countryCode, stateId).subscribe(
                    existingCities => {
                        const cities = {};
                        if (existingCities) {
                            cities[stateId] = Object.values(existingCities);
                            this.citiesData[countryCode] = cities;
                        }
                        resolve(this.citiesData);
                    }
                );
            });
        });
    }

    update(countryCode, state): boolean {
        try {
            const data = {};
            data[state["id"]] = state["name"];
            this.db.list(`/location/state`)
                .update(countryCode, data);

            return true;
        } catch (e) {
            console.log(e);

            return false;
        }
    }

    addStates(countryCode: string, states: Array<any>): boolean {
        try {
            for (const state of states) {
                const data = {};
                data["stateName"] = state["name"];
                data["countryCode"] = state["countryCode"];
                this.store(data);
            }

            return true;
        } catch (e) {
            console.log(e);

            return false;
        }
    }

    async save(data, city): Promise<boolean> {
        try {
            if (data["selectedCountryCode"] && data["selectedStateId"]) {
                if (city) {
                    const dataValue = {};
                    dataValue[city["id"]] = city["name"];
                    this.db
                        .list(`location/city/${data["selectedCountryCode"]}`)
                        .update(data["selectedStateId"], dataValue);
                } else {
                    const existingCities = await this.getFormattedStateCities(data["selectedCountryCode"], data["selectedStateId"]);
                    if (data["newCities"] && data["newCities"].length) {
                        for (const cityData of data["newCities"]) {
                            if (!existingCities) {
                                this.db
                                    .list(`location/city/${data["selectedCountryCode"]}/${data["selectedStateId"]}`)
                                    .push(cityData["name"]);
                            } else {
                                if (!Object.values(existingCities).includes(cityData["name"])) {
                                    this.db
                                        .list(`location/city/${data["selectedCountryCode"]}/${data["selectedStateId"]}`)
                                        .push(cityData["name"]);
                                }
                            }
                        }
                    }
                }

                return true;
            }

            return false;
        } catch (err) {
            console.log(err);

            return false;
        }
    }

    async getFormattedStateCities(countryCode: string, stateId: string): Promise<any> {
        return new Promise(resolve => {
            this.getStateCities(countryCode, stateId)
                .subscribe(cities => {
                    resolve(cities);
                });
        });
    }

    async initNgLgas(): Promise<void> {
        try {
            const statesKeys = await this.getStateKeys();
            if (statesKeys.length) {
                const rawNgLgas = await this.getRawNgLgas();
                const lgaKeys = Object.keys(rawNgLgas);
                for (const lgaKey of lgaKeys) {
                    const stateKey = statesKeys[lgaKey];
                    this.storeLga(stateKey, lgaKey, rawNgLgas);
                }
            }
        } catch (e) {
            console.log(e);
        }
    }

    private async storeLga(stateKey: string, lgaKey: string, rawNgLgas): Promise<void> {
        return await new Promise<void>(resolve => {
            const stateLgas = rawNgLgas[lgaKey.toString()];
            for (const stateLga of stateLgas) {
                this.db.list(`location/city/NG/${stateKey}`).push(stateLga["name"]);
            }

        });
    }

    private async getRawNgLgas(): Promise<any> {
        return await new Promise<any>(resolve => {
            this.db.list(`location/city/NG`)
                .valueChanges()
                .subscribe(ngLgas => {
                    if (ngLgas.length === 0) {
                        const rawNgLgas = this.getNgLgas()["NG"];
                        resolve(rawNgLgas);
                    }
                });
        });
    }

    private async getStateKeys(): Promise<Array<string>> {
        return await new Promise<any>(resolve => {
            this.db.list(`location/country/NG`)
                .valueChanges()
                .subscribe(ngCountry => {
                    if (ngCountry && ngCountry.length) {
                        this.db.list(`location/state/NG`)
                            .snapshotChanges()
                            .subscribe(states => {
                                if (states.length) {
                                    const statesKeys = states.map(state => {
                                        return state.key;
                                    });
                                    resolve(statesKeys);
                                }
                            });
                    }
                });
        });
    }

    getIdsOfCitiesInUse(): Observable<Array<string>> {
        const communities = this.db.list(`/communities`)
            .valueChanges();

        return communities.map(community => {
            return community.map(country => {
                const addressSnap = country["location"]["address"];

                if (addressSnap) {
                    return addressSnap["lgaId"];
                }
            });
        });
    }

    getNgLgas(): any {
        return {
            NG: {
                0: [
                    {
                        code: 0,
                        name: "Aba North"
                    },
                    {
                        code: 1,
                        name: "Aba South"
                    },
                    {
                        code: 2,
                        name: "Arochukwu"
                    },
                    {
                        code: 3,
                        name: "Bende"
                    },
                    {
                        code: 4,
                        name: "Ikwuano"
                    },
                    {
                        code: 5,
                        name: "Isiala Ngwa North"
                    },
                    {
                        code: 6,
                        name: "Isiala Ngwa South"
                    },
                    {
                        code: 7,
                        name: "Isuikwuato"
                    },
                    {
                        code: 8,
                        name: "Obi Ngwa"
                    },
                    {
                        code: 9,
                        name: "Ohafia"
                    },
                    {
                        code: 10,
                        name: "Osisioma NgAwa"
                    },
                    {
                        code: 11,
                        name: "Ugwunagbo"
                    },
                    {
                        code: 12,
                        name: "Ukwa East"
                    },
                    {
                        code: 13,
                        name: "Ukwa West"
                    },
                    {
                        code: 14,
                        name: "Umuahia North"
                    },
                    {
                        code: 15,
                        name: "Umuahia South"
                    },
                    {
                        code: 16,
                        name: "Umu Nneochi"
                    }
                ],
                1: [
                    {
                        code: 0,
                        name: "Gwagwalada"
                    },
                    {
                        code: 1,
                        name: "Kuje"
                    },
                    {
                        code: 2,
                        name: "Abaji"
                    },
                    {
                        code: 3,
                        name: "Abuja Municipal"
                    },
                    {
                        code: 4,
                        name: "Bwari"
                    },
                    {
                        code: 5,
                        name: "Kwali"
                    }
                ],
                2: [
                    {
                        code: 0,
                        name: "Fufore"
                    },
                    {
                        code: 1,
                        name: "Ganye"
                    },
                    {
                        code: 2,
                        name: "Gombi"
                    },
                    {
                        code: 3,
                        name: "Guyuk"
                    },
                    {
                        code: 4,
                        name: "Hong"
                    },
                    {
                        code: 5,
                        name: "Jada"
                    },
                    {
                        code: 6,
                        name: "Shelleng"
                    },
                    {
                        code: 7,
                        name: "Demsa"
                    },
                    {
                        code: 8,
                        name: "Madagali"
                    },
                    {
                        code: 9,
                        name: "Maiha"
                    },
                    {
                        code: 10,
                        name: "Mayo-Belwa"
                    },
                    {
                        code: 11,
                        name: "Michika"
                    },
                    {
                        code: 12,
                        name: "Mubi North"
                    },
                    {
                        code: 13,
                        name: "Numan"
                    },
                    {
                        code: 14,
                        name: "Song"
                    },
                    {
                        code: 15,
                        name: "Mubi South"
                    },
                    {
                        code: 16,
                        name: "Yola North (State capital)"
                    },
                    {
                        code: 17,
                        name: "Yola South"
                    },
                    {
                        code: 18,
                        name: "Girei"
                    },
                    {
                        code: 19,
                        name: "Toungo"
                    },
                    {
                        code: 20,
                        name: "Lamurde"
                    }
                ],
                3: [
                    {
                        code: 0,
                        name: "Abak"
                    },
                    {
                        code: 1,
                        name: "Eastern Obolo"
                    },
                    {
                        code: 2,
                        name: "Eket\tEsit-Eket"
                    },
                    {
                        code: 3,
                        name: "Essien"
                    },
                    {
                        code: 4,
                        name: "Udim"
                    },
                    {
                        code: 5,
                        name: "Etim-Ekpo\t"
                    },
                    {
                        code: 6,
                        name: "Etinan\tIbeno"
                    },
                    {
                        code: 7,
                        name: "Ibesikpo-Asutan"
                    },
                    {
                        code: 8,
                        name: "Ibiono-Ibom"
                    },
                    {
                        code: 9,
                        name: "Ika"
                    },
                    {
                        code: 10,
                        name: "Ikono"
                    },
                    {
                        code: 11,
                        name: "Ikot Abasi"
                    },
                    {
                        code: 12,
                        name: "Ikot Ekpene"
                    },
                    {
                        code: 13,
                        name: "Ini"
                    },
                    {
                        code: 14,
                        name: "Itu"
                    },
                    {
                        code: 15,
                        name: "Mbo"
                    },
                    {
                        code: 16,
                        name: "Mkpat-Enin"
                    },
                    {
                        code: 17,
                        name: "Nsit-Atai"
                    },
                    {
                        code: 18,
                        name: "Nsit-Ibom"
                    },
                    {
                        code: 19,
                        name: "Nsit-Ubium"
                    },
                    {
                        code: 20,
                        name: "Obot-Akara"
                    },
                    {
                        code: 21,
                        name: "Okobo"
                    },
                    {
                        code: 22,
                        name: "Onna"
                    },
                    {
                        code: 23,
                        name: "Oron"
                    },
                    {
                        code: 24,
                        name: "Oruk Anam\tUkanafun"
                    },
                    {
                        code: 25,
                        name: "Udung-Uko"
                    },
                    {
                        code: 26,
                        name: "Uruan"
                    },
                    {
                        code: 27,
                        name: "Urue-Offong/Oruko"
                    },
                    {
                        code: 28,
                        name: "Uyo\t"
                    }
                ],
                4: [
                    {
                        code: 0,
                        name: "Aguata"
                    },
                    {
                        code: 1,
                        name: "Awka North"
                    },
                    {
                        code: 2,
                        name: "Awka South"
                    },
                    {
                        code: 3,
                        name: "Anambra East"
                    },
                    {
                        code: 4,
                        name: "Anambra West"
                    },
                    {
                        code: 5,
                        name: "Anaocha"
                    },
                    {
                        code: 6,
                        name: "Ayamelum"
                    },
                    {
                        code: 7,
                        name: "Dunukofia"
                    },
                    {
                        code: 8,
                        name: "Ekwusigo"
                    },
                    {
                        code: 9,
                        name: "Idemili North"
                    },
                    {
                        code: 10,
                        name: "Idemili South"
                    },
                    {
                        code: 11,
                        name: "Ihiala"
                    },
                    {
                        code: 12,
                        name: "Njikoka"
                    },
                    {
                        code: 13,
                        name: "Nnewi North"
                    },
                    {
                        code: 14,
                        name: "Nnewi South"
                    },
                    {
                        code: 15,
                        name: "Ogbaru"
                    },
                    {
                        code: 16,
                        name: "Onitsha North"
                    },
                    {
                        code: 17,
                        name: "Onitsha South"
                    },
                    {
                        code: 18,
                        name: "Orumba North"
                    },
                    {
                        code: 19,
                        name: "Orumba South"
                    },
                    {
                        code: 20,
                        name: "Oyi"
                    }
                ],
                5: [
                    {
                        code: 0,
                        name: "Tafawa Balewa"
                    },
                    {
                        code: 1,
                        name: "Dass"
                    },
                    {
                        code: 2,
                        name: "Toro\tBogoro"
                    },
                    {
                        code: 3,
                        name: "Ningi"
                    },
                    {
                        code: 4,
                        name: "Warji"
                    },
                    {
                        code: 5,
                        name: "Ganjuwa"
                    },
                    {
                        code: 6,
                        name: "Kirfi"
                    },
                    {
                        code: 7,
                        name: "Alkaler"
                    },
                    {
                        code: 8,
                        name: "Southern region"
                    },
                    {
                        code: 9,
                        name: "Darazo"
                    },
                    {
                        code: 10,
                        name: "Misau"
                    },
                    {
                        code: 11,
                        name: "Giade"
                    },
                    {
                        code: 12,
                        name: "Shira"
                    },
                    {
                        code: 13,
                        name: "Jama are"
                    },
                    {
                        code: 14,
                        name: "Katagum"
                    },
                    {
                        code: 15,
                        name: "Itas/Gadau"
                    },
                    {
                        code: 16,
                        name: "Zaki"
                    },
                    {
                        code: 17,
                        name: "Gamawa"
                    },
                    {
                        code: 18,
                        name: "Damban"
                    }
                ],
                6: [
                    {
                        code: 0,
                        name: "Brass"
                    },
                    {
                        code: 1,
                        name: "Ekeremor"
                    },
                    {
                        code: 2,
                        name: "Kolokuma/Opokuma"
                    },
                    {
                        code: 3,
                        name: "Nembe"
                    },
                    {
                        code: 4,
                        name: "Ogbia"
                    },
                    {
                        code: 5,
                        name: "Sagbama"
                    },
                    {
                        code: 6,
                        name: "Southern Ijaw"
                    },
                    {
                        code: 7,
                        name: "Yenagoa"
                    }
                ],
                7: [
                    {
                        code: 0,
                        name: "Agatu"
                    },
                    {
                        code: 1,
                        name: "Apa"
                    },
                    {
                        code: 2,
                        name: "Ado"
                    },
                    {
                        code: 3,
                        name: "Buruku"
                    },
                    {
                        code: 4,
                        name: "Gboko"
                    },
                    {
                        code: 5,
                        name: "Guma"
                    },
                    {
                        code: 6,
                        name: "Gwer East"
                    },
                    {
                        code: 7,
                        name: "Gwer West"
                    },
                    {
                        code: 8,
                        name: "Katsina-Ala"
                    },
                    {
                        code: 9,
                        name: "Konshisha"
                    },
                    {
                        code: 10,
                        name: "Kwande"
                    },
                    {
                        code: 11,
                        name: "Logo"
                    },
                    {
                        code: 12,
                        name: "Makurdi"
                    },
                    {
                        code: 13,
                        name: "Obi"
                    },
                    {
                        code: 14,
                        name: "Ogbadibo"
                    },
                    {
                        code: 15,
                        name: "Ohimini"
                    },
                    {
                        code: 16,
                        name: "Oju"
                    },
                    {
                        code: 17,
                        name: "Okpokwu"
                    },
                    {
                        code: 18,
                        name: "Otukpo"
                    },
                    {
                        code: 19,
                        name: "Tarka"
                    },
                    {
                        code: 20,
                        name: "Ukum"
                    },
                    {
                        code: 21,
                        name: "Ushongo"
                    },
                    {
                        code: 22,
                        name: "Vandeikya"
                    }
                ],
                8: [
                    {
                        code: 0,
                        name: "Maiduguri"
                    },
                    {
                        code: 1,
                        name: "Askira/Uba"
                    },
                    {
                        code: 2,
                        name: "Abadam"
                    },
                    {
                        code: 3,
                        name: "Ngala"
                    },
                    {
                        code: 4,
                        name: "Bayo"
                    },
                    {
                        code: 5,
                        name: "Gubio"
                    },
                    {
                        code: 6,
                        name: "Kala/Balge"
                    },
                    {
                        code: 7,
                        name: "Biu"
                    },
                    {
                        code: 8,
                        name: "Guzamala"
                    },
                    {
                        code: 9,
                        name: "Mafa"
                    },
                    {
                        code: 10,
                        name: "Chibok"
                    },
                    {
                        code: 11,
                        name: "Kaga"
                    },
                    {
                        code: 12,
                        name: "Konduga"
                    },
                    {
                        code: 13,
                        name: "Damboa"
                    },
                    {
                        code: 14,
                        name: "Kukawa"
                    },
                    {
                        code: 15,
                        name: "Bama"
                    },
                    {
                        code: 16,
                        name: "Gwoza"
                    },
                    {
                        code: 17,
                        name: "Magumeri\tJere"
                    },
                    {
                        code: 18,
                        name: "Hawul"
                    },
                    {
                        code: 19,
                        name: "Marte"
                    },
                    {
                        code: 20,
                        name: "Dikwa"
                    },
                    {
                        code: 21,
                        name: "Kwaya"
                    },
                    {
                        code: 22,
                        name: "Kusar"
                    },
                    {
                        code: 23,
                        name: "Mobbar"
                    },
                    {
                        code: 24,
                        name: "Shani\t"
                    },
                    {
                        code: 25,
                        name: "Monguno"
                    },
                    {
                        code: 26,
                        name: "Nganzai"
                    }
                ],
                9: [
                    {
                        code: 0,
                        name: "Abi"
                    },
                    {
                        code: 1,
                        name: "Akamkpa"
                    },
                    {
                        code: 2,
                        name: "Akpabuyo"
                    },
                    {
                        code: 3,
                        name: "Bakassi"
                    },
                    {
                        code: 4,
                        name: "Bekwarra"
                    },
                    {
                        code: 5,
                        name: "Biase"
                    },
                    {
                        code: 6,
                        name: "Boki"
                    },
                    {
                        code: 7,
                        name: "Calabar Municipal"
                    },
                    {
                        code: 8,
                        name: "Calabar South"
                    },
                    {
                        code: 9,
                        name: "Etung"
                    },
                    {
                        code: 10,
                        name: "Ikom"
                    },
                    {
                        code: 11,
                        name: "Obanliku"
                    },
                    {
                        code: 12,
                        name: "Obubra"
                    },
                    {
                        code: 13,
                        name: "Obudu"
                    },
                    {
                        code: 14,
                        name: "Odukpani"
                    },
                    {
                        code: 15,
                        name: "Ogoja"
                    },
                    {
                        code: 16,
                        name: "Yakuur"
                    },
                    {
                        code: 17,
                        name: "Yala"
                    }
                ],
                10: [
                    {
                        code: 0,
                        name: "Ethiope"
                    },
                    {
                        code: 1,
                        name: "East"
                    },
                    {
                        code: 2,
                        name: "Aniocha North"
                    },
                    {
                        code: 3,
                        name: "Bomadi"
                    },
                    {
                        code: 4,
                        name: "Ethiope"
                    },
                    {
                        code: 5,
                        name: "West\tAniocha South"
                    },
                    {
                        code: 6,
                        name: "Burutu"
                    },
                    {
                        code: 7,
                        name: "Okpe"
                    },
                    {
                        code: 8,
                        name: "Ika North East"
                    },
                    {
                        code: 9,
                        name: "Isoko North"
                    },
                    {
                        code: 10,
                        name: "Sapele"
                    },
                    {
                        code: 11,
                        name: "Ika South"
                    },
                    {
                        code: 12,
                        name: "Isoko South"
                    },
                    {
                        code: 13,
                        name: "Udu"
                    },
                    {
                        code: 14,
                        name: "Ndokwa East"
                    },
                    {
                        code: 15,
                        name: "Patani"
                    },
                    {
                        code: 16,
                        name: "Ughelli North"
                    },
                    {
                        code: 17,
                        name: "Ndokwa West"
                    },
                    {
                        code: 18,
                        name: "Warri North"
                    },
                    {
                        code: 19,
                        name: "Ughelli South"
                    },
                    {
                        code: 20,
                        name: "Oshimili North"
                    },
                    {
                        code: 21,
                        name: "Warri South"
                    },
                    {
                        code: 22,
                        name: "Uvwie"
                    },
                    {
                        code: 23,
                        name: "Oshimili South"
                    },
                    {
                        code: 24,
                        name: "Warri South West"
                    },
                    {
                        code: 25,
                        name: "Ukwuani"
                    }
                ],
                11: [
                    {
                        code: 0,
                        name: "Abakaliki"
                    },
                    {
                        code: 1,
                        name: "Afikpo"
                    },
                    {
                        code: 2,
                        name: "North"
                    },
                    {
                        code: 3,
                        name: "Afikpo South"
                    },
                    {
                        code: 4,
                        name: "Ebonyi"
                    },
                    {
                        code: 5,
                        name: "Ezza North"
                    },
                    {
                        code: 6,
                        name: "Ezza South"
                    },
                    {
                        code: 7,
                        name: "Ikwo"
                    },
                    {
                        code: 8,
                        name: "Ishielu"
                    },
                    {
                        code: 9,
                        name: "Ivo"
                    },
                    {
                        code: 10,
                        name: "Izzi"
                    },
                    {
                        code: 11,
                        name: "Ohaozara"
                    },
                    {
                        code: 12,
                        name: "Ohaukwu"
                    },
                    {
                        code: 13,
                        name: "Onicha"
                    }
                ],
                12: [
                    {
                        code: 0,
                        name: "Akoko Edo"
                    },
                    {
                        code: 1,
                        name: "Orhionmwon"
                    },
                    {
                        code: 2,
                        name: "Ovia North-West"
                    },
                    {
                        code: 3,
                        name: "Ovia South-West"
                    },
                    {
                        code: 4,
                        name: "Esan North-East"
                    },
                    {
                        code: 5,
                        name: "Esan South-East"
                    },
                    {
                        code: 6,
                        name: "Esan Central"
                    },
                    {
                        code: 7,
                        name: "Etsako Central"
                    },
                    {
                        code: 8,
                        name: "Etsako East"
                    },
                    {
                        code: 9,
                        name: "Etsako West"
                    },
                    {
                        code: 10,
                        name: "Oredo Esan west"
                    },
                    {
                        code: 11,
                        name: "Egor Igueben"
                    },
                    {
                        code: 12,
                        name: "Ikpoba Okha Owan East"
                    },
                    {
                        code: 13,
                        name: "Owan West Uhunmwonde"
                    }

                ],
                13: [
                    {
                        code: 0,
                        name: "Ado-Ekiti"
                    },
                    {
                        code: 1,
                        name: "Oye"
                    },
                    {
                        code: 2,
                        name: "Aiyekire (Gbonyin)"
                    },
                    {
                        code: 3,
                        name: "Efon"
                    },
                    {
                        code: 4,
                        name: "Ekiti East"
                    },
                    {
                        code: 5,
                        name: "Ekiti South-West"
                    },
                    {
                        code: 6,
                        name: "Ekiti West"
                    },
                    {
                        code: 7,
                        name: "Emure"
                    },
                    {
                        code: 8,
                        name: "Ido-Osi"
                    },
                    {
                        code: 9,
                        name: "Ijero"
                    },
                    {
                        code: 10,
                        name: "Ikere"
                    },
                    {
                        code: 11,
                        name: "Ikole"
                    },
                    {
                        code: 12,
                        name: "Ilejemeje"
                    },
                    {
                        code: 13,
                        name: "Irepodun/Ifelodun"
                    },
                    {
                        code: 14,
                        name: "Ise/Orun"
                    },
                    {
                        code: 15,
                        name: "Moba"
                    }
                ],
                14: [
                    {
                        code: 0,
                        name: "Aninri"
                    },
                    {
                        code: 1,
                        name: "Awgu"
                    },
                    {
                        code: 2,
                        name: "Enugu East"
                    },
                    {
                        code: 3,
                        name: "Enugu North"
                    },
                    {
                        code: 4,
                        name: "Enugu South"
                    },
                    {
                        code: 5,
                        name: "Ezeagu"
                    },
                    {
                        code: 6,
                        name: "Igbo Etiti"
                    },
                    {
                        code: 7,
                        name: "Igbo Eze North"
                    },
                    {
                        code: 8,
                        name: "Igbo Eze South"
                    },
                    {
                        code: 9,
                        name: "Isi Uzo"
                    },
                    {
                        code: 10,
                        name: "Nkanu East"
                    },
                    {
                        code: 11,
                        name: "Nkanu West"
                    },
                    {
                        code: 12,
                        name: "Nsukka"
                    },
                    {
                        code: 13,
                        name: "Oji River"
                    },
                    {
                        code: 14,
                        name: "Udenu"
                    },
                    {
                        code: 15,
                        name: "Udi"
                    },
                    {
                        code: 16,
                        name: "Uzo Uwani"
                    }
                ],
                15: [
                    {
                        code: 0,
                        name: "Akko"
                    },
                    {
                        code: 1,
                        name: "Balanga"
                    },
                    {
                        code: 2,
                        name: "Billiri"
                    },
                    {
                        code: 3,
                        name: "Dukku"
                    },
                    {
                        code: 4,
                        name: "Funakaye"
                    },
                    {
                        code: 5,
                        name: "Gombe"
                    },
                    {
                        code: 6,
                        name: "Kaltungo"
                    },
                    {
                        code: 7,
                        name: "Kwami"
                    },
                    {
                        code: 8,
                        name: "Nafada\tShongom"
                    }
                ],
                16: [
                    {
                        code: 0,
                        name: "Aboh Mbaise"
                    },
                    {
                        code: 1,
                        name: "Ahiazu Mbaise"
                    },
                    {
                        code: 2,
                        name: "Ehime Mbano"
                    },
                    {
                        code: 3,
                        name: "Ezinihitte"
                    },
                    {
                        code: 4,
                        name: "Ideato North"
                    },
                    {
                        code: 5,
                        name: "Ideato South"
                    },
                    {
                        code: 6,
                        name: "Ihitte/Uboma"
                    },
                    {
                        code: 7,
                        name: "Ikeduru"
                    },
                    {
                        code: 8,
                        name: "Isiala Mbano"
                    },
                    {
                        code: 9,
                        name: "Isu"
                    },
                    {
                        code: 10,
                        name: "Mbaitoli"
                    },
                    {
                        code: 11,
                        name: "Ngor Okpala"
                    },
                    {
                        code: 12,
                        name: "Njaba"
                    },
                    {
                        code: 13,
                        name: "Nkwerre"
                    },
                    {
                        code: 14,
                        name: "Nwangele"
                    },
                    {
                        code: 15,
                        name: "Obowo"
                    },
                    {
                        code: 16,
                        name: "Oguta"
                    },
                    {
                        code: 17,
                        name: "Ohaji/Egbema"
                    },
                    {
                        code: 18,
                        name: "Okigwe"
                    },
                    {
                        code: 19,
                        name: "Onuimo"
                    },
                    {
                        code: 20,
                        name: "Orlu"
                    },
                    {
                        code: 21,
                        name: "Orsu"
                    },
                    {
                        code: 22,
                        name: "Oru East"
                    },
                    {
                        code: 23,
                        name: "Oru West"
                    },
                    {
                        code: 24,
                        name: "Owerri Municipal"
                    },
                    {
                        code: 25,
                        name: "Owerri North"
                    },
                    {
                        code: 26,
                        name: "Owerri West"
                    }
                ],
                17: [
                    {
                        code: 0,
                        name: "Auyo"
                    },
                    {
                        code: 1,
                        name: "Babura"
                    },
                    {
                        code: 2,
                        name: "Biriniwa"
                    },
                    {
                        code: 3,
                        name: "Birnin Kudu"
                    },
                    {
                        code: 4,
                        name: "Buji"
                    },
                    {
                        code: 5,
                        name: "Dutse"
                    },
                    {
                        code: 6,
                        name: "Gagarawa"
                    },
                    {
                        code: 7,
                        name: "Garki"
                    },
                    {
                        code: 8,
                        name: "Gumel"
                    },
                    {
                        code: 9,
                        name: "Guri"
                    },
                    {
                        code: 10,
                        name: "Gwaram"
                    },
                    {
                        code: 11,
                        name: "Gwiwa"
                    },
                    {
                        code: 12,
                        name: "Hadejia"
                    },
                    {
                        code: 13,
                        name: "Jahun"
                    },
                    {
                        code: 14,
                        name: "Kafin Hausa"
                    },
                    {
                        code: 15,
                        name: "Kaugama"
                    },
                    {
                        code: 16,
                        name: "Kazaure"
                    },
                    {
                        code: 17,
                        name: "Kiri Kasama"
                    },
                    {
                        code: 18,
                        name: "Kiyawa"
                    },
                    {
                        code: 19,
                        name: "Maigatari"
                    },
                    {
                        code: 20,
                        name: "Malam Madori"
                    },
                    {
                        code: 21,
                        name: "Miga"
                    },
                    {
                        code: 22,
                        name: "Ringim"
                    },
                    {
                        code: 23,
                        name: "Roni"
                    },
                    {
                        code: 24,
                        name: "Sule Tankarkar"
                    },
                    {
                        code: 25,
                        name: "Taura"
                    },
                    {
                        code: 26,
                        name: "Yankwashi"
                    }
                ],
                18: [
                    {
                        code: 0,
                        name: "Birnin Gwari"
                    },
                    {
                        code: 1,
                        name: "Chikun"
                    },
                    {
                        code: 2,
                        name: "Giwa"
                    },
                    {
                        code: 3,
                        name: "Igabi"
                    },
                    {
                        code: 4,
                        name: "Ikara"
                    },
                    {
                        code: 5,
                        name: "Jaba"
                    },
                    {
                        code: 6,
                        name: "Jema a"
                    },
                    {
                        code: 7,
                        name: "Kachia"
                    },
                    {
                        code: 8,
                        name: "Kaduna"
                    },
                    {
                        code: 9,
                        name: "North"
                    },
                    {
                        code: 10,
                        name: "Kaduna"
                    },
                    {
                        code: 11,
                        name: "South"
                    },
                    {
                        code: 12,
                        name: "Kagarko"
                    },
                    {
                        code: 13,
                        name: "Kajuru"
                    },
                    {
                        code: 14,
                        name: "Kaura"
                    },
                    {
                        code: 15,
                        name: "Kauru"
                    },
                    {
                        code: 16,
                        name: "Kubau"
                    },
                    {
                        code: 17,
                        name: "Kudan"
                    },
                    {
                        code: 18,
                        name: "Lere"
                    },
                    {
                        code: 19,
                        name: "Makarfi"
                    },
                    {
                        code: 20,
                        name: "Sabon Gari"
                    },
                    {
                        code: 21,
                        name: "Sanga"
                    },
                    {
                        code: 22,
                        name: "Soba"
                    },
                    {
                        code: 23,
                        name: "Zangon Kataf"
                    },
                    {
                        code: 24,
                        name: "Zaria"
                    }
                ],
                19: [
                    {
                        code: 0,
                        name: "Fagge"
                    },
                    {
                        code: 1,
                        name: "Dala"
                    },
                    {
                        code: 2,
                        name: "Gwale"
                    },
                    {
                        code: 3,
                        name: "Kano Municipal"
                    },
                    {
                        code: 4,
                        name: "Tarauni"
                    },
                    {
                        code: 5,
                        name: "Nassarawa"
                    },
                    {
                        code: 6,
                        name: "Kumbotso"
                    },
                    {
                        code: 7,
                        name: "Ungogo"
                    },
                    {
                        code: 8,
                        name: "Kano Metropolitan Area"
                    },
                    {
                        code: 9,
                        name: "Dawakin Tofa"
                    },
                    {
                        code: 10,
                        name: "Tofa"
                    },
                    {
                        code: 11,
                        name: "Rimin Gado"
                    },
                    {
                        code: 12,
                        name: "Bagwai"
                    },
                    {
                        code: 13,
                        name: "Gezawa"
                    },
                    {
                        code: 14,
                        name: "Gabasawa"
                    },
                    {
                        code: 15,
                        name: "Minjibir"
                    },
                    {
                        code: 16,
                        name: "Dambatta"
                    },
                    {
                        code: 17,
                        name: "Makoda"
                    },
                    {
                        code: 18,
                        name: "Kunchi"
                    },
                    {
                        code: 19,
                        name: "Bichi"
                    },
                    {
                        code: 20,
                        name: "Tsanyawa"
                    },
                    {
                        code: 21,
                        name: "Shanono"
                    },
                    {
                        code: 22,
                        name: "Gwarzo"
                    },
                    {
                        code: 23,
                        name: "Karaye"
                    },
                    {
                        code: 24,
                        name: "Rogo"
                    },
                    {
                        code: 25,
                        name: "Kabo"
                    },
                    {
                        code: 26,
                        name: "Northern Kano State"
                    },
                    {
                        code: 27,
                        name: "Bunkure"
                    },
                    {
                        code: 28,
                        name: "Kibiya"
                    },
                    {
                        code: 29,
                        name: "Rano"
                    },
                    {
                        code: 30,
                        name: "Tudun"
                    },
                    {
                        code: 31,
                        name: "Wada"
                    },
                    {
                        code: 32,
                        name: "Doguwa"
                    },
                    {
                        code: 33,
                        name: "Madobi"
                    },
                    {
                        code: 34,
                        name: "Kura"
                    },
                    {
                        code: 35,
                        name: "Garun Mallam"
                    },
                    {
                        code: 36,
                        name: "Bebeji"
                    },
                    {
                        code: 37,
                        name: "Kiru"
                    },
                    {
                        code: 38,
                        name: "Sumaila"
                    },
                    {
                        code: 39,
                        name: "Garko"
                    },
                    {
                        code: 40,
                        name: "Takai"
                    },
                    {
                        code: 41,
                        name: "Albasu"
                    },
                    {
                        code: 42,
                        name: "Gaya"
                    },
                    {
                        code: 43,
                        name: "Ajingi"
                    },
                    {
                        code: 44,
                        name: "Wudil"
                    },
                    {
                        code: 45,
                        name: "Warawa"
                    },
                    {
                        code: 46,
                        name: "Dawakin Kudu"
                    },
                    {
                        code: 47,
                        name: "Southern Kano State"
                    },
                    {
                        code: 48,
                        name: "Yamaltu/Deba"
                    }
                ],
                20: [
                    {
                        code: 0,
                        name: "Bakori"
                    },
                    {
                        code: 1,
                        name: "Batagarawa"
                    },
                    {
                        code: 2,
                        name: "Batsari"
                    },
                    {
                        code: 3,
                        name: "Baure"
                    },
                    {
                        code: 4,
                        name: "Bindawa"
                    },
                    {
                        code: 5,
                        name: "Charanchi"
                    },
                    {
                        code: 6,
                        name: "Dan"
                    },
                    {
                        code: 7,
                        name: "Musa"
                    },
                    {
                        code: 8,
                        name: "Dandume"
                    },
                    {
                        code: 9,
                        name: "Danja"
                    },
                    {
                        code: 10,
                        name: "Daura"
                    },
                    {
                        code: 11,
                        name: "Dutsi"
                    },
                    {
                        code: 12,
                        name: "Dutsin-Ma"
                    },
                    {
                        code: 13,
                        name: "Faskari"
                    },
                    {
                        code: 14,
                        name: "Funtua"
                    },
                    {
                        code: 15,
                        name: "Ingawa"
                    },
                    {
                        code: 16,
                        name: "Jibia"
                    },
                    {
                        code: 17,
                        name: "Kafur"
                    },
                    {
                        code: 18,
                        name: "Kaita"
                    },
                    {
                        code: 19,
                        name: "Kankara"
                    },
                    {
                        code: 20,
                        name: "Kankia"
                    },
                    {
                        code: 21,
                        name: "Katsina"
                    },
                    {
                        code: 22,
                        name: "Kurfi"
                    },
                    {
                        code: 23,
                        name: "Kusada"
                    },
                    {
                        code: 24,
                        name: "Mai\"Adua"
                    },
                    {
                        code: 25,
                        name: "Malumfashi"
                    },
                    {
                        code: 26,
                        name: "Mani"
                    },
                    {
                        code: 27,
                        name: "Mashi"
                    },
                    {
                        code: 28,
                        name: "Matazu"
                    },
                    {
                        code: 29,
                        name: "Musawa"
                    },
                    {
                        code: 30,
                        name: "Rimi"
                    },
                    {
                        code: 31,
                        name: "Sabuwa"
                    },
                    {
                        code: 32,
                        name: "Safana"
                    },
                    {
                        code: 33,
                        name: "Sandamu"
                    },
                    {
                        code: 34,
                        name: "Zango"
                    }
                ],
                21: [
                    {
                        code: 0,
                        name: "Aleiro"
                    },
                    {
                        code: 1,
                        name: "Arewa Dandi"
                    },
                    {
                        code: 2,
                        name: "Argungu"
                    },
                    {
                        code: 3,
                        name: "Augie"
                    },
                    {
                        code: 4,
                        name: "Bagudo"
                    },
                    {
                        code: 5,
                        name: "Birnin Kebbi"
                    },
                    {
                        code: 6,
                        name: "Bunza"
                    },
                    {
                        code: 7,
                        name: "Dandi"
                    },
                    {
                        code: 8,
                        name: "Fakai"
                    },
                    {
                        code: 9,
                        name: "Gwandu"
                    },
                    {
                        code: 10,
                        name: "Jega"
                    },
                    {
                        code: 11,
                        name: "Kalgo"
                    },
                    {
                        code: 12,
                        name: "Koko/Besse"
                    },
                    {
                        code: 13,
                        name: "Maiyama"
                    },
                    {
                        code: 14,
                        name: "Ngaski"
                    },
                    {
                        code: 15,
                        name: "Sakaba"
                    },
                    {
                        code: 16,
                        name: "Shanga"
                    },
                    {
                        code: 17,
                        name: "Suru"
                    },
                    {
                        code: 18,
                        name: "Wasagu/Danko"
                    },
                    {
                        code: 19,
                        name: "Yauri"
                    },
                    {
                        code: 20,
                        name: "Zuru"
                    }
                ],
                22: [
                    {
                        code: 0,
                        name: "Adavi"
                    },
                    {
                        code: 1,
                        name: "Ajaokuta"
                    },
                    {
                        code: 2,
                        name: "Ankpa"
                    },
                    {
                        code: 3,
                        name: "Bassa"
                    },
                    {
                        code: 4,
                        name: "Dekina"
                    },
                    {
                        code: 5,
                        name: "Ibaji"
                    },
                    {
                        code: 6,
                        name: "Idah"
                    },
                    {
                        code: 7,
                        name: "Igalamela-Odolu"
                    },
                    {
                        code: 8,
                        name: "Ijumu"
                    },
                    {
                        code: 9,
                        name: "Kabba/Bunu"
                    },
                    {
                        code: 10,
                        name: "Kogi"
                    },
                    {
                        code: 11,
                        name: "Lokoja"
                    },
                    {
                        code: 12,
                        name: "Mopa-Muro"
                    },
                    {
                        code: 13,
                        name: "Ofu"
                    },
                    {
                        code: 14,
                        name: "Ogori/Magongo"
                    },
                    {
                        code: 15,
                        name: "Okehi"
                    },
                    {
                        code: 16,
                        name: "Okene"
                    },
                    {
                        code: 17,
                        name: "Olamaboro"
                    },
                    {
                        code: 18,
                        name: "Omala"
                    },
                    {
                        code: 19,
                        name: "Yagba East"
                    },
                    {
                        code: 20,
                        name: "Yagba West"
                    }
                ],
                23: [
                    {
                        code: 0,
                        name: "Asa"
                    },
                    {
                        code: 1,
                        name: "Baruten"
                    },
                    {
                        code: 2,
                        name: "Edu"
                    },
                    {
                        code: 3,
                        name: "Ekiti"
                    },
                    {
                        code: 4,
                        name: "Ifelodun"
                    },
                    {
                        code: 5,
                        name: "Ilorin East"
                    },
                    {
                        code: 6,
                        name: "Ilorin South"
                    },
                    {
                        code: 7,
                        name: "Ilorin West"
                    },
                    {
                        code: 8,
                        name: "Irepodun"
                    },
                    {
                        code: 9,
                        name: "Isin"
                    },
                    {
                        code: 10,
                        name: "Kaiama"
                    },
                    {
                        code: 11,
                        name: "Moro"
                    },
                    {
                        code: 12,
                        name: "Offa"
                    },
                    {
                        code: 13,
                        name: "Oke Ero"
                    },
                    {
                        code: 14,
                        name: "Oyun"
                    },
                    {
                        code: 15,
                        name: "Pategi"
                    }
                ],
                24: [
                    {
                        code: 0,
                        name: "Agege"
                    },
                    {
                        code: 1,
                        name: "Alimosho"
                    },
                    {
                        code: 2,
                        name: "Ifako-Ijaye"
                    },
                    {
                        code: 3,
                        name: "Ikeja"
                    },
                    {
                        code: 4,
                        name: "Kosofe"
                    },
                    {
                        code: 5,
                        name: "Mushin"
                    },
                    {
                        code: 6,
                        name: "Oshodi-Isolo"
                    },
                    {
                        code: 7,
                        name: "Shomolu"
                    },
                    {
                        code: 8,
                        name: "Ikeja Division"
                    },
                    {
                        code: 9,
                        name: "Apapa"
                    },
                    {
                        code: 10,
                        name: "Eti-Osa"
                    },
                    {
                        code: 11,
                        name: "Lagos Island"
                    },
                    {
                        code: 12,
                        name: "Lagos Mainland"
                    },
                    {
                        code: 13,
                        name: "Surulere"
                    },
                    {
                        code: 14,
                        name: "Lagos Division"
                    },
                    {
                        code: 15,
                        name: "Ajeromi-Ifelodun"
                    },
                    {
                        code: 16,
                        name: "Amuwo-Odofin"
                    },
                    {
                        code: 17,
                        name: "Ojo"
                    },
                    {
                        code: 18,
                        name: "Badagry"
                    },
                    {
                        code: 19,
                        name: "Badagry Division"
                    },
                    {
                        code: 20,
                        name: "Ikorodu"
                    },
                    {
                        code: 21,
                        name: "Ikorodu Division"
                    },
                    {
                        code: 22,
                        name: "Ibeju-Lekki"
                    },
                    {
                        code: 23,
                        name: "Epe"
                    },
                    {
                        code: 24,
                        name: "Epe Division"
                    }
                ],
                25: [
                    {
                        code: 0,
                        name: "Karu"
                    },
                    {
                        code: 1,
                        name: "Akwanga"
                    },
                    {
                        code: 2,
                        name: "Awe"
                    },
                    {
                        code: 3,
                        name: "Keffi"
                    },
                    {
                        code: 4,
                        name: "Nasarawa"
                    },
                    {
                        code: 5,
                        name: "Egon"
                    },
                    {
                        code: 6,
                        name: "Doma"
                    },
                    {
                        code: 7,
                        name: "Kokona"
                    },
                    {
                        code: 8,
                        name: "Wamba"
                    },
                    {
                        code: 9,
                        name: "Keana"
                    },
                    {
                        code: 10,
                        name: "Nasarawa West"
                    },
                    {
                        code: 11,
                        name: "Lafia"
                    },
                    {
                        code: 12,
                        name: "Obi"
                    }
                ],
                26: [
                    {
                        code: 0,
                        name: "Agaie"
                    },
                    {
                        code: 1,
                        name: "Agwara"
                    },
                    {
                        code: 2,
                        name: "Bida"
                    },
                    {
                        code: 3,
                        name: "Borgu"
                    },
                    {
                        code: 4,
                        name: "Bosso"
                    },
                    {
                        code: 5,
                        name: "Chanchaga"
                    },
                    {
                        code: 6,
                        name: "Edati"
                    },
                    {
                        code: 7,
                        name: "Gbako"
                    },
                    {
                        code: 8,
                        name: "Gurara"
                    },
                    {
                        code: 9,
                        name: "Katcha"
                    },
                    {
                        code: 10,
                        name: "Kontagora"
                    },
                    {
                        code: 11,
                        name: "Lapai"
                    },
                    {
                        code: 12,
                        name: "Lavun"
                    },
                    {
                        code: 13,
                        name: "Magama"
                    },
                    {
                        code: 14,
                        name: "Mariga"
                    },
                    {
                        code: 15,
                        name: "Mashegu"
                    },
                    {
                        code: 16,
                        name: "Mokwa"
                    },
                    {
                        code: 17,
                        name: "Munya"
                    },
                    {
                        code: 18,
                        name: "Paikoro"
                    },
                    {
                        code: 19,
                        name: "Rafi"
                    },
                    {
                        code: 20,
                        name: "Rijau"
                    },
                    {
                        code: 21,
                        name: "Shiroro"
                    },
                    {
                        code: 22,
                        name: "Suleja"
                    },
                    {
                        code: 23,
                        name: "Tafa"
                    },
                    {
                        code: 24,
                        name: "Wushishi"
                    },
                    {
                        code: 25,
                        name: "Ogun"
                    },
                    {
                        code: 26,
                        name: "State"
                    },
                    {
                        code: 27,
                        name: "Agaie"
                    },
                    {
                        code: 28,
                        name: "Agwara"
                    },
                    {
                        code: 29,
                        name: "Bida"
                    },
                    {
                        code: 30,
                        name: "Borgu"
                    },
                    {
                        code: 31,
                        name: "Bosso"
                    },
                    {
                        code: 32,
                        name: "Chanchaga"
                    },
                    {
                        code: 33,
                        name: "Edati"
                    },
                    {
                        code: 34,
                        name: "Gbako"
                    },
                    {
                        code: 35,
                        name: "Gurara"
                    },
                    {
                        code: 36,
                        name: "Katcha"
                    },
                    {
                        code: 37,
                        name: "Kontagora"
                    },
                    {
                        code: 38,
                        name: "Lapai"
                    },
                    {
                        code: 39,
                        name: "Lavun"
                    },
                    {
                        code: 40,
                        name: "Magama"
                    },
                    {
                        code: 41,
                        name: "Mariga"
                    },
                    {
                        code: 42,
                        name: "Mashegu"
                    },
                    {
                        code: 43,
                        name: "Mokwa"
                    },
                    {
                        code: 44,
                        name: "Munya"
                    },
                    {
                        code: 45,
                        name: "Paikoro"
                    },
                    {
                        code: 46,
                        name: "Rafi"
                    },
                    {
                        code: 47,
                        name: "Rijau"
                    },
                    {
                        code: 48,
                        name: "Shiroro"
                    },
                    {
                        code: 49,
                        name: "Suleja"
                    },
                    {
                        code: 50,
                        name: "Tafa"
                    },
                    {
                        code: 51,
                        name: "Wushishi"
                    }
                ],
                27: [
                    {
                        code: 0,
                        name: "Abeokuta North"
                    },
                    {
                        code: 1,
                        name: "Abeokuta South"
                    },
                    {
                        code: 2,
                        name: "Ado-Odo/Ota"
                    },
                    {
                        code: 3,
                        name: "Ewekoro"
                    },
                    {
                        code: 4,
                        name: "Ifo"
                    },
                    {
                        code: 5,
                        name: "Ijebu East"
                    },
                    {
                        code: 6,
                        name: "Ijebu North"
                    },
                    {
                        code: 7,
                        name: "Ijebu North East"
                    },
                    {
                        code: 8,
                        name: "Ijebu Ode"
                    },
                    {
                        code: 9,
                        name: "Ikenne"
                    },
                    {
                        code: 10,
                        name: "Imeko Afon"
                    },
                    {
                        code: 11,
                        name: "Ipokia"
                    },
                    {
                        code: 12,
                        name: "Obafemi Owode"
                    },
                    {
                        code: 13,
                        name: "Odogbolu"
                    },
                    {
                        code: 14,
                        name: "Odeda"
                    },
                    {
                        code: 15,
                        name: "Ogun Waterside"
                    },
                    {
                        code: 16,
                        name: "Remo North"
                    },
                    {
                        code: 17,
                        name: "Shagamu"
                    },
                    {
                        code: 18,
                        name: "Yewa North"
                    },
                    {
                        code: 19,
                        name: "Yewa South"
                    }
                ],
                28: [
                    {
                        code: 0,
                        name: "Akoko North-East"
                    },
                    {
                        code: 1,
                        name: "Akoko North-West"
                    },
                    {
                        code: 2,
                        name: "Akoko South-East"
                    },
                    {
                        code: 3,
                        name: "Akoko South-West"
                    },
                    {
                        code: 4,
                        name: "Akure North"
                    },
                    {
                        code: 5,
                        name: "Akure South"
                    },
                    {
                        code: 6,
                        name: "Ese Odo"
                    },
                    {
                        code: 7,
                        name: "Idanre"
                    },
                    {
                        code: 8,
                        name: "Ifedore"
                    },
                    {
                        code: 9,
                        name: "Ilaje"
                    },
                    {
                        code: 10,
                        name: "Ile Oluji/Okeigbo"
                    },
                    {
                        code: 11,
                        name: "Irele"
                    },
                    {
                        code: 12,
                        name: "Odigbo"
                    },
                    {
                        code: 13,
                        name: "Okitipupa"
                    },
                    {
                        code: 14,
                        name: "Ondo East"
                    },
                    {
                        code: 15,
                        name: "Ondo West"
                    },
                    {
                        code: 16,
                        name: "Ose"
                    },
                    {
                        code: 17,
                        name: "Owo"
                    }
                ],
                29: [
                    {
                        code: 0,
                        name: "Aiyedaade"
                    },
                    {
                        code: 1,
                        name: "Aiyedire"
                    },
                    {
                        code: 2,
                        name: "Atakunmosa East"
                    },
                    {
                        code: 3,
                        name: "Atakunmosa West"
                    },
                    {
                        code: 4,
                        name: "Boluwaduro"
                    },
                    {
                        code: 5,
                        name: "Boripe Iragbiji"
                    },
                    {
                        code: 6,
                        name: "Ede North"
                    },
                    {
                        code: 7,
                        name: "Ede South"
                    },
                    {
                        code: 8,
                        name: "Egbedore"
                    },
                    {
                        code: 9,
                        name: "Ejigbo"
                    },
                    {
                        code: 10,
                        name: "Ife Central"
                    },
                    {
                        code: 11,
                        name: "Ife East"
                    },
                    {
                        code: 12,
                        name: "Ife North"
                    },
                    {
                        code: 13,
                        name: "Ife South"
                    },
                    {
                        code: 14,
                        name: "Ifedayo"
                    },
                    {
                        code: 15,
                        name: "Ifelodun"
                    },
                    {
                        code: 16,
                        name: "Ila"
                    },
                    {
                        code: 17,
                        name: "Ilesa East"
                    },
                    {
                        code: 18,
                        name: "Ilesa West"
                    },
                    {
                        code: 19,
                        name: "Irepodun"
                    },
                    {
                        code: 20,
                        name: "Irewole"
                    },
                    {
                        code: 21,
                        name: "Isokan"
                    },
                    {
                        code: 22,
                        name: "Iwo"
                    },
                    {
                        code: 23,
                        name: "Obokun"
                    },
                    {
                        code: 24,
                        name: "Odo"
                    },
                    {
                        code: 25,
                        name: "Otin"
                    },
                    {
                        code: 26,
                        name: "Ola"
                    },
                    {
                        code: 27,
                        name: "Oluwa"
                    },
                    {
                        code: 28,
                        name: "Olorunda"
                    },
                    {
                        code: 29,
                        name: "Oriade"
                    },
                    {
                        code: 30,
                        name: "Orolu"
                    },
                    {
                        code: 31,
                        name: "Osogbo"
                    }
                ],
                30: [
                    {
                        code: 0,
                        name: "Akinyele Moniya"
                    },
                    {
                        code: 1,
                        name: "Afijio Jobele"
                    },
                    {
                        code: 2,
                        name: "Egbeda Egbeda"
                    },
                    {
                        code: 3,
                        name: "Ibadan North Agodi Gate"
                    },
                    {
                        code: 4,
                        name: "Ibadan North-East Iwo Road"
                    },
                    {
                        code: 5,
                        name: "Ibadan North-West"
                    },
                    {
                        code: 6,
                        name: "Ibadan South-West Ring Road"
                    },
                    {
                        code: 7,
                        name: "Ibadan South-East Mapo"
                    },
                    {
                        code: 8,
                        name: "Ibarapa Central"
                    },
                    {
                        code: 9,
                        name: "Ibarapa East Eruwa"
                    },
                    {
                        code: 10,
                        name: "Ido"
                    },
                    {
                        code: 11,
                        name: "Irepo"
                    },
                    {
                        code: 12,
                        name: "Iseyin iseyin"
                    },
                    {
                        code: 13,
                        name: "Kajola"
                    },
                    {
                        code: 14,
                        name: "Lagelu"
                    },
                    {
                        code: 15,
                        name: "Ogbomosho North"
                    },
                    {
                        code: 16,
                        name: "Ogbomosho South"
                    },
                    {
                        code: 17,
                        name: "Oyo West Ojongbodu"
                    },
                    {
                        code: 18,
                        name: "Atiba Offa Meta"
                    },
                    {
                        code: 19,
                        name: "Atisbo Tede"
                    },
                    {
                        code: 20,
                        name: "Saki West"
                    },
                    {
                        code: 21,
                        name: "Saki East"
                    },
                    {
                        code: 22,
                        name: "Itesiwaju Otu"
                    },
                    {
                        code: 23,
                        name: "Iwajowa"
                    },
                    {
                        code: 24,
                        name: "Ibarapa North"
                    },
                    {
                        code: 25,
                        name: "Olorunsogo"
                    },
                    {
                        code: 26,
                        name: "Oluyole"
                    },
                    {
                        code: 27,
                        name: "Ogo Oluwa"
                    },
                    {
                        code: 28,
                        name: "Surulere"
                    },
                    {
                        code: 29,
                        name: "Orelope"
                    },
                    {
                        code: 30,
                        name: "Ori Ire"
                    },
                    {
                        code: 31,
                        name: "Oyo East"
                    },
                    {
                        code: 32,
                        name: "Ona Ara"
                    }
                ],
                31: [
                    {
                        code: 0,
                        name: "Barkin Ladi"
                    },
                    {
                        code: 1,
                        name: "Bassa"
                    },
                    {
                        code: 2,
                        name: "Bokkos"
                    },
                    {
                        code: 3,
                        name: "Jos East"
                    },
                    {
                        code: 4,
                        name: "Jos North"
                    },
                    {
                        code: 5,
                        name: "Jos South"
                    },
                    {
                        code: 6,
                        name: "Kanam"
                    },
                    {
                        code: 7,
                        name: "Kanke"
                    },
                    {
                        code: 8,
                        name: "Langtang North"
                    },
                    {
                        code: 9,
                        name: "Langtang South"
                    },
                    {
                        code: 10,
                        name: "Mangu"
                    },
                    {
                        code: 11,
                        name: "Mikang"
                    },
                    {
                        code: 12,
                        name: "Pankshin"
                    },
                    {
                        code: 13,
                        name: "Qua\"an Pan"
                    },
                    {
                        code: 14,
                        name: "Riyom"
                    },
                    {
                        code: 15,
                        name: "Shendam"
                    },
                    {
                        code: 16,
                        name: "Wase"
                    }
                ],
                32: [
                    {
                        code: 0,
                        name: "Abua/Odual"
                    },
                    {
                        code: 1,
                        name: "Ahoada East"
                    },
                    {
                        code: 2,
                        name: "Ahoada West"
                    },
                    {
                        code: 3,
                        name: "Akuku-Toru"
                    },
                    {
                        code: 4,
                        name: "Andoni"
                    },
                    {
                        code: 5,
                        name: "Asari-Toru"
                    },
                    {
                        code: 6,
                        name: "Bonny"
                    },
                    {
                        code: 7,
                        name: "Degema"
                    },
                    {
                        code: 8,
                        name: "Eleme"
                    },
                    {
                        code: 9,
                        name: "Emohua"
                    },
                    {
                        code: 10,
                        name: "Etche\tGokana"
                    },
                    {
                        code: 11,
                        name: "Ikwerre"
                    },
                    {
                        code: 12,
                        name: "Khana"
                    },
                    {
                        code: 13,
                        name: "Obio-Akpor\tOgba/Egbema/Ndoni"
                    },
                    {
                        code: 14,
                        name: "Ogu/Bolo"
                    },
                    {
                        code: 15,
                        name: "Okrika"
                    },
                    {
                        code: 16,
                        name: "Omuma\tOpobo/Nkoro"
                    },
                    {
                        code: 17,
                        name: "Oyigbo"
                    },
                    {
                        code: 18,
                        name: "Port Harcourt"
                    }
                ],
                33: [
                    {
                        code: 0,
                        name: "Binji"
                    },
                    {
                        code: 1,
                        name: "Bodinga"
                    },
                    {
                        code: 2,
                        name: "Dange Shuni"
                    },
                    {
                        code: 3,
                        name: "Gada"
                    },
                    {
                        code: 4,
                        name: "Goronyo"
                    },
                    {
                        code: 5,
                        name: "Gudu"
                    },
                    {
                        code: 6,
                        name: "Gwadabawa"
                    },
                    {
                        code: 7,
                        name: "Illela"
                    },
                    {
                        code: 8,
                        name: "Isa"
                    },
                    {
                        code: 9,
                        name: "Kebbe"
                    },
                    {
                        code: 10,
                        name: "Kware"
                    },
                    {
                        code: 11,
                        name: "Rabah"
                    },
                    {
                        code: 12,
                        name: "Sabon Birni"
                    },
                    {
                        code: 13,
                        name: "Shagari"
                    },
                    {
                        code: 14,
                        name: "Silame"
                    },
                    {
                        code: 15,
                        name: "Sokoto North"
                    },
                    {
                        code: 16,
                        name: "Sokoto South"
                    },
                    {
                        code: 17,
                        name: "Tambuwal"
                    },
                    {
                        code: 18,
                        name: "Tangaza"
                    },
                    {
                        code: 19,
                        name: "Tureta"
                    },
                    {
                        code: 20,
                        name: "Wamako"
                    },
                    {
                        code: 21,
                        name: "Wurno"
                    },
                    {
                        code: 22,
                        name: "Yabo"
                    }
                ],
                34: [
                    {
                        code: 0,
                        name: "Binji"
                    },
                    {
                        code: 1,
                        name: "Bodinga"
                    },
                    {
                        code: 2,
                        name: "Dange Shuni"
                    },
                    {
                        code: 3,
                        name: "Gada"
                    },
                    {
                        code: 4,
                        name: "Goronyo"
                    },
                    {
                        code: 5,
                        name: "Gudu"
                    },
                    {
                        code: 6,
                        name: "Gwadabawa"
                    },
                    {
                        code: 7,
                        name: "Illela"
                    },
                    {
                        code: 8,
                        name: "Isa"
                    },
                    {
                        code: 9,
                        name: "Kebbe"
                    },
                    {
                        code: 10,
                        name: "Kware"
                    },
                    {
                        code: 11,
                        name: "Rabah"
                    },
                    {
                        code: 12,
                        name: "Sabon Birni"
                    },
                    {
                        code: 13,
                        name: "Shagari"
                    },
                    {
                        code: 14,
                        name: "Silame"
                    },
                    {
                        code: 15,
                        name: "Sokoto North"
                    },
                    {
                        code: 16,
                        name: "Sokoto South"
                    },
                    {
                        code: 17,
                        name: "Tambuwal"
                    },
                    {
                        code: 18,
                        name: "Tangaza"
                    },
                    {
                        code: 19,
                        name: "Tureta"
                    },
                    {
                        code: 20,
                        name: "Wamako"
                    },
                    {
                        code: 21,
                        name: "Wurno"
                    },
                    {
                        code: 22,
                        name: "Yabo"
                    }
                ],
                35: [
                    {
                        code: 0,
                        name: "Bursari"
                    },
                    {
                        code: 1,
                        name: "Damaturu"
                    },
                    {
                        code: 2,
                        name: "Geidam"
                    },
                    {
                        code: 3,
                        name: "Bade"
                    },
                    {
                        code: 4,
                        name: "Gujba"
                    },
                    {
                        code: 5,
                        name: "Gulani"
                    },
                    {
                        code: 6,
                        name: "Fika"
                    },
                    {
                        code: 7,
                        name: "Fune"
                    },
                    {
                        code: 8,
                        name: "Jakusko"
                    },
                    {
                        code: 9,
                        name: "Karasuwa"
                    },
                    {
                        code: 10,
                        name: "Machina"
                    },
                    {
                        code: 11,
                        name: "Nangere"
                    },
                    {
                        code: 12,
                        name: "Nguru"
                    },
                    {
                        code: 13,
                        name: "Potiskum"
                    },
                    {
                        code: 14,
                        name: "Tarmuwa"
                    },
                    {
                        code: 15,
                        name: "Yunusari"
                    },
                    {
                        code: 16,
                        name: "Yusufari"
                    }
                ],
                36: [
                    {
                        code: 0,
                        name: "Anka"
                    },
                    {
                        code: 1,
                        name: "Bakura"
                    },
                    {
                        code: 2,
                        name: "Birnin Magaji/Kiyaw"
                    },
                    {
                        code: 3,
                        name: "Bukkuyum"
                    },
                    {
                        code: 4,
                        name: "Bungudu"
                    },
                    {
                        code: 5,
                        name: "tsafe"
                    },
                    {
                        code: 6,
                        name: "Gummi"
                    },
                    {
                        code: 7,
                        name: "Gusau"
                    },
                    {
                        code: 8,
                        name: "Kaura Namoda"
                    },
                    {
                        code: 9,
                        name: "Maradun"
                    },
                    {
                        code: 10,
                        name: "Maru"
                    },
                    {
                        code: 11,
                        name: "Shinkafi"
                    },
                    {
                        code: 12,
                        name: "Talata Mafara"
                    },
                    {
                        code: 13,
                        name: "Zurmi"
                    }
                ]
            }
        };
    }

}



// WEBPACK FOOTER //
// ./src/app/shared/location/city.service.ts